import { visit } from 'unist-util-visit';

import { ensureEmojiCache, getEmojiByShortcode, getEmojiByUnified } from './emojiCache';
import { entityIdFromRichdownRefMatch, escapeEntityAttr, richdownEntityRefRegex } from './richdownEntityRefRegex';

/**
 * remark plugin to parse text entity references and convert them to <entity> tags for Streamdown
 */
export function remarkEntityPlugin() {
  return (tree) => {
    visit(tree, 'text', (node, index, parent) => {
      if (!node.value || !parent) return;

      // only known richdown entity types (avoid swallowing e.g. [note:foo] or markdown links)
      const text = node.value;
      const entityRegex = richdownEntityRefRegex();

      // find all matches
      const matches = Array.from(text.matchAll(entityRegex));
      if (matches.length === 0) return;

      // build new nodes by processing all matches in order
      const newNodes = [];
      let lastIndex = 0;

      for (const match of matches) {
        // add text before this match
        if (match.index > lastIndex) {
          const before = text.slice(lastIndex, match.index);
          if (before) newNodes.push({ type: 'text', value: before });
        }

        // create entity representation
        let representation = '';
        const entityType = match[1].toLowerCase();
        const entityId = entityIdFromRichdownRefMatch(match);
        const safeTarget = escapeEntityAttr(entityId);
        switch (entityType) {
          case 'profile':
            representation = `<entity data-type="profile" data-target="${safeTarget}"></entity>`;
            break;
          case 'channel':
            representation = `<entity data-type="channel" data-target="${safeTarget}"></entity>`;
            break;
          case 'spammer':
            representation = `<entity data-type="spammer" data-target="${safeTarget}"></entity>`;
            break;
          case 'emoji':
            // emoji expansion requires the cache since the data doesn't match the entity parser's expectations
            ensureEmojiCache();

            // the match is normally the unified code point instead of the shortcode
            let emoji = getEmojiByUnified(entityId);

            // try shortcode if it wasn't a unified code point
            if (!emoji) emoji = getEmojiByShortcode(entityId);

            // fallback to the raw value if neither lookup worked
            representation = `<entity data-type="emoji" data-id="${escapeEntityAttr(emoji?.id ?? entityId)}"></entity>`;
            break;
          default:
            break;
        }

        if (representation) {
          newNodes.push({ type: 'html', value: representation });
        } else {
          newNodes.push({ type: 'text', value: match[0] });
        }
        lastIndex = match.index + match[0].length;
      }

      // add remaining text after last match
      if (lastIndex < text.length) {
        const after = text.slice(lastIndex);
        if (after) newNodes.push({ type: 'text', value: after });
      }

      // replace the text node with the new nodes
      parent.children.splice(index, 1, ...newNodes);
    });
  };
}
